import { useState, useEffect, useMemo } from 'react';
import {
  FiSearch,
  FiMenu,
  FiX,
  FiLogOut,
  FiUser,
  FiSettings,
  FiChevronDown,
  FiLoader,
  FiMessageSquare,
  FiStar,
  FiMail,
  FiZap
} from 'react-icons/fi';
import { FaTelegram } from 'react-icons/fa';
import { Button } from './Button';
import { formatUsdc } from '../utils/formatUtils';
import { useI18n } from '../i18n/useI18n';

const TG_GROUP_TOOLTIP_KEY = 'polyone_tg_group_tooltip_shown';

// 检测是否是有效的以太坊地址格式
const isValidEthAddress = (address) => {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

export function Header({
  dockOpen,
  isAuthenticated,
  userId,
  userMenuOpen,
  membershipTier,
  userProfile,
  points,
  pointsLoading,
  searchTerm,
  searchLoading,
  showSearchDropdown,
  searchResults,
  searchContainerRef,
  onDockToggle,
  onUserMenuToggle,
  onSearchChange,
  onSearchFocus,
  onSearchKeyDown,
  onSelectSearchResult,
  onSelectTrader,
  onLoginOpen,
  onPreferenceOpen,
  onLogout,
  totalUsdcBalance,
  isWalletBalanceLoading,
  onMembershipOpen,
  onPointsOpen,
  onEmailBindOpen
}) {
  const { t, lang, toggleLang } = useI18n();
  const { markets = [], traders = [] } = searchResults || {};
  const hasResults = markets.length > 0 || traders.length > 0;
  const tierKey = (membershipTier || 'free').toLowerCase();
  const tierLabelMap = {
    free: t('membership.free'),
    lite: t('membership.lite'),
    pro: t('membership.pro'),
  };
  const membershipLabel = tierLabelMap[tierKey] || membershipTier || t('membership.free');
  const profileName = userProfile?.display_name || userProfile?.username || '';
  const profileUsername = userProfile?.username ? `@${userProfile.username}` : '';
  const profileAvatar = userProfile?.avatar_url || '';
  const profileEmail = userProfile?.email || '';
  const hasEmail = Boolean(profileEmail);
  const pointsBalance = Number(points?.balance ?? 0);

  const maskedEmail = useMemo(() => {
    if (!profileEmail || !profileEmail.includes('@')) return '';
    const [name, domain] = profileEmail.split('@');
    if (!name) return profileEmail;
    const visible = name.length <= 2 ? name[0] : name.slice(0, 2);
    return `${visible}***@${domain}`;
  }, [profileEmail]);
  
  // Telegram 群组提示状态
  const [showTgTooltip, setShowTgTooltip] = useState(false);
  
  useEffect(() => {
    const hasShown = localStorage.getItem(TG_GROUP_TOOLTIP_KEY);
    if (!hasShown) {
      // 延迟显示，避免页面加载时立即弹出
      const timer = setTimeout(() => {
        setShowTgTooltip(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const handleTgTooltipClose = () => {
    setShowTgTooltip(false);
    localStorage.setItem(TG_GROUP_TOOLTIP_KEY, 'true');
  };
  
  const handleTgGroupClick = () => {
    handleTgTooltipClose();
    window.open('https://t.me/polyone_ai', '_blank');
  };
  
  // 检测搜索词是否是有效地址，且不在搜索结果中
  const trimmedSearch = (searchTerm || '').trim();
  const isAddressSearch = isValidEthAddress(trimmedSearch);
  const addressInResults = traders.some(t => t.address?.toLowerCase() === trimmedSearch.toLowerCase());
  const showDirectAddressOption = isAddressSearch && !addressInResults && !searchLoading;
  return (
    <header className="glass-lg flex items-center justify-between gap-2 md:gap-4 px-3 md:px-6 py-3 mx-3 md:mx-6 lg:mx-8 mt-3 md:mt-6 lg:mt-6 rounded-2xl z-[100] relative border border-white/10 shadow-2xl">
      {/* Left Section: Toggle & Logo */}
      <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
        <button
          className="w-10 h-10 flex items-center justify-center bg-slate-800/50 border border-slate-700/50 rounded-xl cursor-pointer transition-all duration-200 flex-shrink-0 text-slate-300 hover:text-white hover:border-sky-500/50 hover:bg-sky-500/10 active:scale-95"
          onClick={onDockToggle}
          aria-label={t('header.navToggle')}
        >
          {dockOpen ? <FiX className="text-xl" /> : <FiMenu className="text-xl" />}
        </button>

        <div className="hidden md:flex items-center gap-0.5 font-sans select-none cursor-pointer">
          <span className="text-lg md:text-xl font-semibold text-white pointer-events-none">Poly</span>
          <span className="text-lg md:text-xl font-semibold px-1 rounded-md bg-[#ff9000] text-black ml-0.5 pointer-events-none">One</span>
        </div>
      </div>

      {/* Right Section: Actions */}
      <div className="flex items-center gap-2 md:gap-3 flex-shrink-0 ml-auto">
        {/* Language Switch */}
        <button
          type="button"
          className="h-9 w-9 md:h-10 md:w-auto md:px-3 inline-flex items-center justify-center bg-slate-800/60 border border-slate-700/50 text-slate-400 rounded-xl cursor-pointer transition-all duration-200 hover:bg-slate-700/80 hover:text-white hover:border-slate-600 active:scale-95 text-xs font-semibold tracking-wide"
          aria-label={t('header.switchLang')}
          onClick={toggleLang}
        >
          <span className="hidden md:inline">{lang === 'zh' ? 'EN' : '中'}</span>
          <span className="md:hidden">{lang === 'zh' ? 'E' : '中'}</span>
        </button>

        {/* Telegram Button */}
        <div className="relative">
          <button
            className="h-9 w-9 md:h-10 md:w-10 inline-flex items-center justify-center bg-gradient-to-br from-[#0088cc]/20 to-[#0088cc]/5 border border-[#0088cc]/30 text-[#0088cc] rounded-xl cursor-pointer transition-all duration-200 hover:bg-[#0088cc]/30 hover:border-[#0088cc]/50 hover:shadow-[0_0_15px_rgba(0,136,204,0.3)] active:scale-95"
            aria-label={t('header.joinTgGroup')}
            onClick={handleTgGroupClick}
          >
            <FaTelegram className="text-lg" />
          </button>
          {/* Telegram 群组提示气泡 */}
          {showTgTooltip && (
            <>
              <div
                className="fixed inset-0 z-[198]"
                onClick={handleTgTooltipClose}
              />
              <div className="absolute top-full right-0 mt-3 w-56 glass-lg rounded-xl border border-sky-500/30 shadow-2xl z-[200] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 ring-1 ring-sky-500/20 p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-sky-500/20 text-sky-400 flex-shrink-0">
                    <FaTelegram className="text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white mb-1">
                      {t('header.tgGroupTitle')}
                    </div>
                    <div className="text-xs text-slate-400">
                      {t('header.tgGroupDesc')}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <span
                    className="text-xs text-sky-400 hover:text-sky-300 transition-colors cursor-pointer"
                    onClick={handleTgTooltipClose}
                  >
                    {t('common.close')}
                  </span>
                </div>
                {/* 小三角箭头 */}
                <div className="absolute -top-2 right-4 w-4 h-4 bg-slate-900/95 border-l border-t border-sky-500/30 transform rotate-45" />
              </div>
            </>
          )}
        </div>

        {isAuthenticated ? (
          <div className="flex items-center gap-2 relative">
            <div className="relative">
              <button
                onClick={onUserMenuToggle}
                className="h-9 md:h-10 pl-2 pr-3 inline-flex items-center gap-2 bg-slate-800/60 border border-slate-700/50 hover:border-slate-600 hover:bg-slate-700/80 text-white rounded-xl cursor-pointer transition-all duration-200 active:scale-95"
              >
                {/* Avatar */}
                {profileAvatar ? (
                  <img
                    src={profileAvatar}
                    alt="avatar"
                    className="w-6 h-6 md:w-7 md:h-7 rounded-full object-cover ring-2 ring-slate-600/50"
                  />
                ) : (
                  <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center text-[10px] font-bold text-white shadow-lg">
                    {(profileName || 'U').slice(0, 1).toUpperCase()}
                  </div>
                )}

                {/* Balance */}
                <div className="flex items-center gap-1.5">
                  {isWalletBalanceLoading ? (
                    <FiLoader className="animate-spin text-slate-400 text-sm" />
                  ) : (
                    <>
                      <span className="hidden md:inline text-sm font-semibold">{formatUsdc(totalUsdcBalance)}</span>
                      <span className="hidden md:inline text-xs text-slate-400">USDC</span>
                    </>
                  )}
                  {/* Points */}
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/25 bg-amber-500/10 px-2 py-0.5 text-[11px] font-mono text-amber-200">
                    <FiZap className="text-amber-300" />
                    {pointsLoading ? '--' : pointsBalance.toLocaleString()}
                  </span>
                  <FiChevronDown className={`text-slate-500 text-sm transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {/* User Dropdown */}
              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-[199]"
                    onClick={onUserMenuToggle}
                  />
                  <div className="absolute top-full right-0 mt-3 w-60 bg-[#0f172a] rounded-2xl border border-slate-700/60 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8),0_0_0_1px_rgba(255,255,255,0.05)] z-[200] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 p-2">
                    {/* 顶部渐变光效 */}
                    <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-indigo-500/40 via-sky-500/40 to-transparent" />
                    
                    {/* 用户信息卡片 */}
                    {(profileName || profileUsername || profileAvatar) && (
                      <div className="px-3 py-3 flex items-center gap-3 bg-gradient-to-r from-slate-800/40 via-transparent to-slate-800/40 rounded-xl mb-1">
                        {profileAvatar ? (
                          <img
                            src={profileAvatar}
                            alt={profileName || profileUsername || 'avatar'}
                            className="w-12 h-12 rounded-full object-cover ring-2 ring-slate-600/50"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center text-sm font-bold text-white shadow-lg">
                            {(profileName || 'U').slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-white font-semibold truncate">
                            {profileName || profileUsername}
                          </div>
                          {profileUsername && profileName && (
                            <div className="text-xs text-slate-400 truncate">{profileUsername}</div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* 邮箱信息 */}
                    {maskedEmail && (
                      <div className="px-3 py-2">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-slate-500">{t('header.boundEmail')}</span>
                          <span className="text-indigo-200 font-mono">{maskedEmail}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* 分隔线 - 带渐变 */}
                    <div className="h-px bg-gradient-to-r from-transparent via-slate-700/80 to-transparent my-2" />
                    
                    {/* 会员等级 */}
                    <div className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500 font-medium">{t('membership.currentTier')}</span>
                        <span className={`
                          text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full
                          ${tierKey === 'pro' ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-300 border border-amber-500/30 shadow-[0_0_12px_rgba(245,158,11,0.15)]' : 
                            tierKey === 'lite' ? 'bg-gradient-to-r from-sky-500/20 to-indigo-500/20 text-sky-300 border border-sky-500/30 shadow-[0_0_12px_rgba(14,165,233,0.15)]' : 
                            'bg-slate-800 text-slate-400 border border-slate-700'}
                        `}>
                          {membershipLabel}
                        </span>
                      </div>
                    </div>

                    {/* 点数余额 */}
                    <div className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500 font-medium">{t('points.balance')}</span>
                        <span className="text-[11px] font-mono text-slate-200">
                          {pointsLoading ? '--' : pointsBalance.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    
                    {/* 操作按钮组 */}
                    <div className="space-y-1 mt-1">
                      {onPointsOpen && (
                        <button
                          className="w-full px-3 py-2.5 text-left text-sm flex items-center gap-3 text-slate-300 hover:text-white hover:bg-amber-500/10 transition-all duration-200 rounded-lg group"
                          onClick={() => {
                            onUserMenuToggle();
                            onPointsOpen();
                          }}
                        >
                          <div className="w-8 h-8 rounded-lg bg-slate-800/80 border border-slate-700/50 flex items-center justify-center text-amber-300 group-hover:text-amber-200 group-hover:border-amber-500/30 group-hover:bg-amber-500/10 transition-all">
                            <FiZap className="text-sm" />
                          </div>
                          <span className="font-medium">{t('points.buy')}</span>
                        </button>
                      )}
                      {onEmailBindOpen && (
                        <button
                          className="w-full px-3 py-2.5 text-left text-sm flex items-center gap-3 text-slate-300 hover:text-white hover:bg-indigo-500/10 transition-all duration-200 rounded-lg group"
                          onClick={() => {
                            onUserMenuToggle();
                            onEmailBindOpen();
                          }}
                        >
                          <div className="w-8 h-8 rounded-lg bg-slate-800/80 border border-slate-700/50 flex items-center justify-center text-indigo-400 group-hover:text-indigo-300 group-hover:border-indigo-500/30 group-hover:bg-indigo-500/10 transition-all">
                            <FiMail className="text-sm" />
                          </div>
                          <span className="font-medium">{hasEmail ? t('header.changeEmail') : t('header.bindEmail')}</span>
                        </button>
                      )}

                      {tierKey !== 'pro' && (
                      <button
                        className="w-full px-3 py-2.5 text-left text-sm flex items-center gap-3 text-slate-300 hover:text-white hover:bg-sky-500/10 transition-all duration-200 rounded-lg group"
                        onClick={() => {
                          onUserMenuToggle();
                          onMembershipOpen?.();
                        }}
                      >
                        <div className="w-8 h-8 rounded-lg bg-slate-800/80 border border-slate-700/50 flex items-center justify-center text-sky-400 group-hover:text-sky-300 group-hover:border-sky-500/30 group-hover:bg-sky-500/10 transition-all">
                          <FiStar className="text-sm" />
                        </div>
                        <span className="font-medium">{t('membership.upgrade')}</span>
                      </button>
                      )}
                    </div>
                    
                    {/* 底部分隔线 */}
                    <div className="h-px bg-gradient-to-r from-transparent via-slate-700/80 to-transparent my-2" />
                    
                    {/* 退出登录 */}
                    <button
                      className="w-full px-3 py-2.5 text-left text-sm flex items-center gap-3 text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-all duration-200 rounded-lg group"
                      onClick={onLogout}
                    >
                      <div className="w-8 h-8 rounded-lg bg-slate-800/80 border border-slate-700/50 flex items-center justify-center text-rose-400/70 group-hover:text-rose-400 group-hover:border-rose-500/30 group-hover:bg-rose-500/10 transition-all">
                        <FiLogOut className="text-sm" />
                      </div>
                      <span className="font-medium">{t('common.logout')}</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <button
            onClick={onLoginOpen}
            className="h-9 md:h-10 px-4 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white text-sm font-semibold rounded-xl cursor-pointer transition-all duration-200 shadow-lg shadow-sky-500/20 hover:shadow-sky-500/30 active:scale-95"
          >
            <FiUser className="w-4 h-4" />
            <span className="hidden md:inline">{t('common.login')}</span>
          </button>
        )}
      </div>
    </header>
  );
}
